import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  InviteInfoResponse,
  PasswordResetRequest,
  UserProfileUpdate,
  UserRegistrationResponse,
  UserLoginResponse,
  ChangeUserPasswordRequest,
} from '../../models-to-replace/auth';
import type { MatchInviteResponse } from '../../type-attendee/match/models';
import { USER_TAGS } from '../../tags';
import { apiBaseUrl } from 'lib.shared/src/api';

export const userAuth = createApi({
  reducerPath: USER_TAGS.USER,
  baseQuery: fetchBaseQuery({
    baseUrl: apiBaseUrl,
  }),
  tagTypes: [USER_TAGS.USER],
  endpoints: (build) => ({
    // AUTHENTIFICATION
    getUser: build.query<UserLoginResponse, void>({
      query: () => ({
        url: '/login/user',
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
      providesTags: [USER_TAGS.USER],
    }),
    getInviteByEventHash: build.query<InviteInfoResponse, string>({
      query: (event_hash: string) => ({
        url: `register/invite/${event_hash}`,
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
    }),

    // the Match.Express outreach link, read while the visitor is still a guest —
    // sibling of getInviteByEventHash, not part of the authenticated match slice
    getMatchInviteByToken: build.query<MatchInviteResponse, string>({
      query: (token: string) => ({
        url: `register/match-invite/${token}`,
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
    }),

    createEditorUser: build.mutation<UserRegistrationResponse, { email: string; password: string }>(
      {
        query: ({ email, password }) => ({
          body: {
            email,
            password,
          },
          method: 'POST',
          url: '/register',
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        }),
        invalidatesTags: [USER_TAGS.USER],
      },
    ),
    createUserAccount: build.mutation<
      UserRegistrationResponse,
      { email: string; password: string; onboarding?: string }
    >({
      query: ({ email, password, onboarding }) => ({
        body: {
          email,
          password,
          remember: true,
          onboarding,
        },
        method: 'POST',
        url: '/register',
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
      invalidatesTags: [USER_TAGS.USER],
    }),
    //TODO
    // createUser: build.mutation<UserRegistrationResponse, UserCreate>({
    //     query: (body) => ({
    //         body: {
    //             email: body.email,
    //             password: body.password
    //         },
    //         method: 'POST',
    //         url: '/register',
    //         headers: {
    //             Accept: 'application/json', // Specify JSON response
    //         },
    //     }),
    //     invalidatesTags: [userauth],
    // }),
    logout: build.mutation<UserLoginResponse, void>({
      query: () => ({
        method: `POST`,
        url: `/logout`,
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
    }),
    passUpdate: build.mutation<any, { password: string }>({
      query: (body) => ({
        body,
        method: `PATCH`,
        url: `/register/password`,
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
    }),
    /** Emails a sign-in link (the passwordless way in); the answer never says whether the account exists. */
    requestLoginLink: build.mutation<
      { data: { email: string; expires_minutes: number } },
      { email: string; redirect?: string }
    >({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/login-link',
        headers: { Accept: 'application/json' },
      }),
    }),
    /** Signs in with the link's token — a click on the link's page, never the link itself. */
    consumeLoginLink: build.mutation<UserLoginResponse, { token: string }>({
      query: ({ token }) => ({
        method: 'POST',
        url: `/login-link/${encodeURIComponent(token)}`,
        headers: { Accept: 'application/json' },
      }),
      invalidatesTags: [USER_TAGS.USER],
    }),
    /** Signs in with the 6-digit code from the link's email instead of its link. */
    consumeLoginCode: build.mutation<UserLoginResponse, { email: string; code: string }>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/login-link/code',
        headers: { Accept: 'application/json' },
      }),
      invalidatesTags: [USER_TAGS.USER],
    }),
    /** A fresh link to the address a used or expired link went to. */
    resendLoginLink: build.mutation<
      { data: { email: string; expires_minutes: number } },
      { token: string; redirect?: string }
    >({
      query: ({ token, redirect }) => ({
        body: { redirect },
        method: 'POST',
        url: `/login-link/${encodeURIComponent(token)}/resend`,
        headers: { Accept: 'application/json' },
      }),
    }),
    loginUser: build.mutation<UserLoginResponse, { email: string; password: string }>({
      query: (body) => ({
        body: { ...body, remember: true },
        method: 'POST',
        url: '/login',
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
      invalidatesTags: [USER_TAGS.USER],
    }),
    // authFB: build.query<any, void>({
    //     query: () => ({
    //         url: `/login/auth/facebook`,
    //         headers: {
    //             Accept: 'application/json', // Specify JSON response
    //         },
    //     }),
    //     providesTags: [userauth]
    // }),
    // authGoogle: build.query<any, void>({
    //     query: () => ({
    //         url: `/login/auth/google`,
    //         headers: {
    //             Accept: 'application/json', // Specify JSON response
    //         },
    //     }),
    //     providesTags: [userauth]
    // }),
    // authUser: build.query<any, { social: string }>({
    //     query: ({social}) => ({
    //         url: `/login/auth/${social}`,
    //         headers: {
    //             Accept: 'application/json', // Specify JSON response
    //         },
    //     }),
    //     providesTags: [userauth]
    // }),
    //PROFILE
    patchUserProfile: build.mutation<UserLoginResponse, UserProfileUpdate>({
      query: (body) => ({
        url: '/user',
        method: 'PATCH',
        body,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: [USER_TAGS.USER],
    }),

    passwordRecover: build.mutation<void, { email: string }>({
      query: (body) => ({
        body: { ...body },
        method: 'POST',
        url: '/forgot-password',
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
      invalidatesTags: [USER_TAGS.USER],
    }),
    passwordReset: build.mutation<void, PasswordResetRequest>({
      query: (body) => ({
        url: '/reset-password',
        method: 'POST',
        body,
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
      invalidatesTags: [USER_TAGS.USER],
    }),
    passwordChange: build.mutation<void, { password: string; password_confirmation: string }>({
      query: (body) => ({
        url: '/change-password',
        method: 'POST',
        body,
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
      invalidatesTags: [USER_TAGS.USER],
    }),
    passwordUpdate: build.mutation<void, ChangeUserPasswordRequest>({
      query: (body) => ({
        url: '/user/change-password',
        method: 'POST',
        body,
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
      invalidatesTags: [USER_TAGS.USER],
    }),

    postAcceptTerms: build.mutation<void, void>({
      query: () => ({
        url: '/user/accept-terms',
        method: 'POST',
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
      invalidatesTags: [USER_TAGS.USER],
    }),
  }),
});

export const {
  useCreateEditorUserMutation,
  // useCreateUserMutation,
  useLogoutMutation,
  useLazyGetUserQuery,
  useLoginUserMutation,
  useRequestLoginLinkMutation,
  useConsumeLoginLinkMutation,
  useConsumeLoginCodeMutation,
  useResendLoginLinkMutation,
  usePassUpdateMutation,
  // useGetUserQuery,
  useGetInviteByEventHashQuery,
  useLazyGetInviteByEventHashQuery,
  useGetMatchInviteByTokenQuery,
  usePasswordChangeMutation,
  // useAuthFBQuery,
  // useAuthGoogleQuery,
  // useAuthUserQuery,
  // useUpdateUserMutation,
  // useGetUser2Query,
  usePatchUserProfileMutation,
  usePasswordRecoverMutation,
  usePasswordResetMutation,
  usePasswordUpdateMutation,
  useCreateUserAccountMutation,
  usePostAcceptTermsMutation,
} = userAuth;
